import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect } from 'react';
import { generateFilterStateKey } from '@atlassian/notification-common';
import { useExperienceTracker } from '@atlassian/notification-common/experience-tracking';
import { LoadingState } from '@atlassian/notification-common/types';
import { useNotificationGroupsClient } from '../../../use-notification-groups-client';
import { useNotificationStateFilters } from '../../../use-notification-list-filters-store/store';
import { RequestExpand } from '../../../utils/inbox-client/types';
import { useNotificationActions, useNotificationsLoadingState } from '../../store';
import calculateContinuationToken from '../../utils/calculate-continuation-token';
import { createRenderableNotificationGroups } from '../../utils/create-renderable-notifications';
import handleExperienceFailures from '../../utils/handle-experience-failures';
export var useNotificationGroupStore = function useNotificationGroupStore(_ref) {
  var locale = _ref.locale,
    isFullPage = _ref.isFullPage;
  var experienceTracker = useExperienceTracker();
  // load in our store and reducer actions
  // use a custom selector for filtering our notifications data
  var _useNotificationState = useNotificationStateFilters(),
    _useNotificationState2 = _slicedToArray(_useNotificationState, 1),
    filters = _useNotificationState2[0];
  var _useNotificationsLoad = useNotificationsLoadingState(filters),
    _useNotificationsLoad2 = _slicedToArray(_useNotificationsLoad, 1),
    currentCategoryState = _useNotificationsLoad2[0];
  var _useNotificationActio = useNotificationActions(),
    _useNotificationActio2 = _slicedToArray(_useNotificationActio, 2),
    updateGroups = _useNotificationActio2[1].updateGroups;
  var _useNotificationGroup = useNotificationGroupsClient({
      locale: locale,
      filters: filters,
      expand: RequestExpand.NONE
    }),
    withoutContentLoadingState = _useNotificationGroup.loadingState,
    withoutContentData = _useNotificationGroup.data,
    notificationsFetch = _useNotificationGroup.fetchData,
    withoutContentError = _useNotificationGroup.error;
  var _useNotificationGroup2 = useNotificationGroupsClient({
      locale: locale,
      filters: filters,
      expand: RequestExpand.CONTENT // WILL return with rich content attached to notifications
    }),
    withContentLoadingState = _useNotificationGroup2.loadingState,
    withContentData = _useNotificationGroup2.data,
    contentFetch = _useNotificationGroup2.fetchData,
    withContentError = _useNotificationGroup2.error;
  useEffect(function () {
    var loadingStates = {
      withoutContent: withoutContentLoadingState,
      withContent: withContentLoadingState
    };
    var dataFilters = loadingStates.withContent === LoadingState.COMPLETE ? withContentData.args.filters : withoutContentData.args.filters;

    // If the filters change during the loading state, the promises will still fulfill and update the data.
    // Ignore data we don't care about for the current filter state
    if (generateFilterStateKey(filters) !== generateFilterStateKey(dataFilters)) {
      return;
    }
    var continuationToken = calculateContinuationToken(loadingStates, withoutContentData.data.continuationToken, withContentData.data.continuationToken);
    var renderableGroups = createRenderableNotificationGroups(loadingStates, withoutContentData.data.groups, withContentData.data.groups);
    updateGroups({
      loadingStates: loadingStates,
      groups: renderableGroups,
      filters: filters,
      continuationToken: continuationToken
    });
  }, [updateGroups, withContentData, withoutContentData, withoutContentLoadingState, withContentLoadingState, filters]);
  useEffect(function () {
    var loadingStates = {
      withoutContent: withoutContentLoadingState,
      withContent: withContentLoadingState
    };
    if (withoutContentError || withContentError) {
      handleExperienceFailures(experienceTracker, loadingStates, withoutContentError, withContentError);
    }
  }, [withContentLoadingState, withoutContentLoadingState, experienceTracker, withContentError, withoutContentError]);

  // continuation tokens are different per category
  var continuationToken = currentCategoryState.continuationToken;

  // fetch both with and without content data in the same handler
  var fetchData = useCallback(function () {
    // increase our pagination limit after the initial fetch
    var limit = isFullPage ? 50 : continuationToken ? 16 : 8;
    notificationsFetch({
      filters: filters,
      continuationToken: continuationToken,
      limit: limit
    });
    contentFetch({
      filters: filters,
      continuationToken: continuationToken,
      limit: limit
    });
  }, [filters, continuationToken, contentFetch, notificationsFetch, isFullPage]);

  // fetch the most recent notifications
  var fetchMostRecentData = useCallback(function (numberOfNotifications) {
    var limit = numberOfNotifications;
    notificationsFetch({
      filters: filters,
      continuationToken: undefined,
      limit: limit
    });
    contentFetch({
      filters: filters,
      continuationToken: undefined,
      limit: limit
    });
  }, [filters, contentFetch, notificationsFetch]);
  var fetchState = currentCategoryState.fetchState;
  return {
    fetchState: fetchState,
    fetchData: fetchData,
    fetchMostRecentData: fetchMostRecentData,
    error: withContentError || withoutContentError,
    firstRequestCompleted: fetchState === LoadingState.COMPLETE || withContentLoadingState === LoadingState.COMPLETE
  };
};